import React, { useState, useContext } from "react";
import IBankAccounts from "./IBankAccounts";
import BankAccountBox from "./components/BankAccountBox/BankAccountBox";
import StepButtons from "@appComponents/StepButtons/StepButtons";
import ConfirmForm from "@appComponents/ConfirmForm/ConfirmForm";
import BankForm from "../BankForm/BankForm";
import FundsService from "@services/Funds.service";
import InfoBox from "@appComponents/InfoBox/InfoBox";
import { TReducers } from "@features/reducers";
import { connect } from "react-redux";
import { useBankAccounts } from "./useBankAccounts";
import {
  animateScrollTo,
  catchError,
  normalizeBankAccountNumber,
} from "@features/utils/functions";
import { IBankAccountProperties } from "../WithdrawAmount/IWithdrawAmount";
import { mappedCountries } from "@features/staticData/countries";
import {
  deleteBankAccount,
  updateBankAccounts,
} from "@funds/features/action.funds";
import { FundsAppContext, FundsDispatchContext } from "@features/context";
import Confirmation from "@appComponents/Confirmation/Confirmation";
import EmailIcon from "@assets/svg/Secondary_market/email-svg.svg";
import { Tooltip } from "antd";
import NoticeIcon from "@funds/pages/MyFunds/pages/StakingPage/parts/StakeForm/danger.svg";
import Notice from "@funds/pages/MyFunds/pages/StakingPage/parts/shared/Notice";
import { Link } from "react-router-dom";

import * as walletStyle from "../CryptoWallets/CryptoWalletsStyle.module.scss";
import * as wAmountStyles from "../WithdrawAmount/WithdrawAmountStyle.module.scss";
import * as bankAccountBoxStyle from "./components/BankAccountBox/BankAccountBoxStyle.module.scss";
import * as styles from "./BankAccountsStyles.module.scss";
import { isTier1AndAbove } from "@funds/features/helpers/functions";
import { RestrictedContainer } from "@appComponents/RestrictedContainer/RestrictedContainer";
import { BFID_URL } from "@bundles/consts";
import { Button } from "@appComponents/Button";
import { Tier1Restriction } from "./components/Tier1Restriction";

const BankAccounts: React.FunctionComponent<IBankAccounts> = (props) => {
  const { investorId } = useContext(FundsAppContext);
  const { values, errors, dispatch } = useBankAccounts();
  const [disabledButton, setDisabledButton] = useState(false);
  const [accountIdToRemove, setAccountIdToRemove] = useState(0);
  const [showCheckEmail, setShowCheckEmail] = useState(false);
  const [addBankAccountLoading, setAddBankAccountLoading] = useState(false);
  const requireBankStatement =
    props.bankAccounts.length === 0 || !!values.bankStatement;

  const editAccountClick = (account: IBankAccountProperties) => {
    dispatch({
      type: "editMode",
      payload: {
        ...account,
        editMode: true,
        country: mappedCountries.find((el) => el.value === account.country),
      },
    });

    animateScrollTo(undefined, document.getElementById("bankaccounts"), 20);
  };

  const addBankAccount = () => {
    if (values.bic_swift && values.account_number && requireBankStatement) {
      setDisabledButton(true);
      setAddBankAccountLoading(true);
      FundsService.addBankAccount(investorId!, {
        bic_swift: values.bic_swift,
        account_number: normalizeBankAccountNumber(values.account_number),
        currency: "bfusd",
        bank_statement: values?.bankStatement,
      })
        .then((res) => {
          props.updateBankAccounts(res);
          dispatch({ type: "addedBankAccount", id: res.data.id });
          setDisabledButton(false);
          setShowCheckEmail(true);
          animateScrollTo(
            undefined,
            document.getElementById("rc-tabs-1-panel-bank")
          );
        })
        .catch((err) => {
          catchError(err);
          const errors: { [key: string]: string } = Object.keys(
            err.response.data.errors
          ).reduce(
            (prev, curr) => ({
              ...prev,
              [curr]: err.response.data.errors[curr][0],
            }),
            {}
          );
          dispatch({ type: "setErrors", errors: errors });
          setDisabledButton(false);
          dispatch({ type: "setInitial" });
        })
        .finally(() => setAddBankAccountLoading(false));
    }
  };

  const renderBankAccounts = () => {
    const requestedAccount = !!props.bankAccounts.find(
      (a) => a.status === "pending" || a.status === "email_sent"
    );

    const getType = (account: IBankAccountProperties) => {
      if (account.status === "active") {
        if (requestedAccount) return "view";
        return "edit";
      }
      return "view_cancel";
    };

    const accounts = props.bankAccounts.map((el, i) => (
      <BankAccountBox
        type={getType(el)}
        editAccountClick={editAccountClick}
        key={i}
        account={el}
        setAccountIdToRemove={setAccountIdToRemove}
        label={
          el.status === "pending" ? (
            <p className={bankAccountBoxStyle.requestedLabel}>
              Subjected to security review
            </p>
          ) : el.status === "email_sent" ? (
            <p className={bankAccountBoxStyle.requestedLabel}>
              Confirmation email sent
            </p>
          ) : el.status === "requires_additional_data" ? (
            <p className={bankAccountBoxStyle.requestedLabel}>
              Require additional data
            </p>
          ) : undefined
        }
      >
        {el.status === "pending" && (
          <InfoBox>
            This account is currently being reviewed. Once approved you will be
            able to use this account for withdrawals/deposits. Note: you can
            only have one active account on your profile.
          </InfoBox>
        )}
        {el.status === "email_sent" && (
          <InfoBox>
            This account is not yet confirmed. Please check your inbox and
            confirm this bank account.
          </InfoBox>
        )}
      </BankAccountBox>
    ));

    return (
      <>
        <h2 className={walletStyle.headline}>Your bank account</h2>
        {props.companyInvestorId && (
          <Notice
            message="Note!"
            icon={<NoticeIcon />}
            description={
              <>
                <div>
                  To withdraw funds in fiat, please contact support so we can
                  coordinate the withdrawal with our partner bank.
                  Alternatively, you can select the USDT option for withdrawals.
                </div>
                <Link
                  className={styles.supportButton}
                  target="_blank"
                  to="/support"
                >
                  <Button>Contact Support</Button>
                </Link>
              </>
            }
            type="warning"
            showIcon
            style={{ margin: "40px auto" }}
          />
        )}

        {!props.companyInvestorId && (
          <>
            {accounts.length > 0 ? (
              <div className={styles.buttonWrapper}>
                {accounts}
                <Tooltip
                  title="Due to company rules, you are only allowed to have one bank
              account associated to your account at any time. Changing bank
              account will replace your currently active account."
                >
                  <Button
                    onClick={() => {
                      editAccountClick(props.bankAccounts[0]);
                    }}
                  >
                    Click here to change your bank account
                  </Button>
                </Tooltip>
              </div>
            ) : (
              <>
                <Notice
                  message="Note!"
                  icon={<NoticeIcon />}
                  description="You have not registered a bank account yet. Please submit a
              request below to add your Bank Account. Note we are only able
              to process withdrawals/deposits to/from one account in your
              name."
                  type="warning"
                  showIcon
                  style={{ margin: "40px auto" }}
                />

                {renderAddBtn("Add New Account")}
              </>
            )}
          </>
        )}
      </>
    );
  };

  const addAccountClick = () => {
    dispatch({
      type: "setInitial",
      payload: { editMode: true },
    });
    animateScrollTo(undefined, document.getElementById("bankaccounts"), 20);
  };

  const prevStep = () => {
    dispatch({ type: "setInitial" });
  };

  const rejectAccount = (bankAccountId: number) => {
    if (investorId) {
      FundsService.rejectBankAccount(investorId, bankAccountId)
        .then(() => {
          setAccountIdToRemove(0);
          props.deleteBankAccount(bankAccountId);
        })
        .catch((err) => {
          setAccountIdToRemove(0);
          catchError(err);
          window.scrollTo(0, 0);
        });
    }
  };

  const renderAddBtn = (text: string) => (
    <p onClick={addAccountClick} className={wAmountStyles.addNew}>
      <span>+</span>
      {text}
    </p>
  );

  const renderForm = () =>
    showCheckEmail ? (
      <>
        <Confirmation
          data={{
            title: "One more step",
            texts: [
              props.bankAccounts.length <= 1
                ? "Please check your email to confirm your new bank account"
                : "Please check your email to confirm the change to your bank account",
            ],
            link1: {
              text: "Back to Account",
              url: "",
              onClick: (e) => {
                e.preventDefault();
                setShowCheckEmail(false);
                dispatch({ type: "setInitial" });
                animateScrollTo(
                  undefined,
                  document.getElementById("bankaccounts"),
                  20
                );
              },
            },
          }}
          icon={<EmailIcon className={styles.emailIcon} />}
        />
      </>
    ) : isTier1AndAbove(props.investorTier) ? (
      <Tier1Restriction />
    ) : (
      <>
        <FundsDispatchContext.Provider value={dispatch}>
          <BankForm values={values} errors={errors} />
        </FundsDispatchContext.Provider>
        <StepButtons
          nextStep={addBankAccount}
          prevStep={prevStep}
          isFirstStep={false}
          isLoading={addBankAccountLoading || false}
          nextHidden={!props.bankFormAddressDataAvailable}
          nextStepEnable={
            Object.keys(errors).length === 0 &&
            !disabledButton &&
            requireBankStatement
          }
          nextText={values.id ? "Save" : "Add"}
        />
      </>
    );

  return (
    <div id="bankaccounts">
      {values.editMode ? renderForm() : renderBankAccounts()}
      <ConfirmForm
        closeLightbox={() => setAccountIdToRemove(0)}
        actionFunction={() => rejectAccount(accountIdToRemove)}
        values={{
          headline:
            "Are you sure you wish to reject the review of this account?",
          paragraph: `Please note that this operation is irreversible.`,
          actionButton: "Confirm",
        }}
        isVisible={!!accountIdToRemove}
      />
    </div>
  );
};

const mapDispatchToProps = (dispatch: Function) => ({
  updateBankAccounts: (data: IBankAccountProperties) =>
    dispatch(updateBankAccounts(data)),
  deleteBankAccount: (accountId: number) =>
    dispatch(deleteBankAccount(accountId)),
});

const mapStateToProps = (store: TReducers) => ({
  bankAccounts: store.fundsReducer.bankAccounts || [],
  bankFormAddressDataAvailable: store.fundsReducer.bankFormAddressDataAvailable,
  companyInvestorId: store.companyReducer.companyDetails.investor_id,
  investorTier: store.userInfo.tier,
});

export default connect(mapStateToProps, mapDispatchToProps)(BankAccounts);
